import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Form } from 'antd';

import { Button, CaptchaErrorAlert, GoogleCaptcha, Input, YandexCaptcha } from 'components';

import useForm from 'hooks/forms/useForm';
import useValidation from 'hooks/forms/useValidation';
import useNotifications from 'hooks/common/useNotifications';
import useGoogleCaptcha from 'hooks/captcha/google/useGoogleCaptcha';
import useYandexCaptcha from 'hooks/captcha/yandex/useYandexCaptcha';
import useGoogleCaptchaHandler from 'hooks/captcha/google/useGoogleCaptchaHandler';

import { sendResetPasswordLink } from 'services/profile.service';
import checkIsCaptchaFinalError from 'utils/captcha/google/checkIsCaptchaFinalError';

import { CAPTCHA_TYPE } from 'config';
import { ErrorType, GoogleCaptchaErrorType } from 'constants/errors';
import { Y_CAPTCHA_PARAM_KEY } from 'constants/captcha/yandex';

import { CaptchaType } from 'types/captcha';
import { IForgotPasswordFormFields, IForgotPasswordFormProps } from './ForgotPassword.types';

import './ForgotPasswordForm.styles.scss';

const ForgotPasswordForm = ({ onFinish }: IForgotPasswordFormProps) => {
  const rule = useValidation();
  const { t } = useTranslation();
  const { notificationApi } = useNotifications();

  const [form] = Form.useForm<IForgotPasswordFormFields>();

  const { submitDisabled } = useForm(form);
  const captchaV2 = Form.useWatch(['captchaV2'], form);

  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const { onCaptchaError: onGoogleCaptchaError } = useGoogleCaptchaHandler({ setError });
  const googleCaptcha = useGoogleCaptcha('forgotPassword', { disabled: CAPTCHA_TYPE !== CaptchaType.GOOGLE });

  const onSubmitFinish = async ({ captchaV2, ...values }: IForgotPasswordFormFields) => {
    setLoading(true);

    let body = values;

    if (CAPTCHA_TYPE === CaptchaType.GOOGLE) {
      body = { ...values, ...googleCaptcha.getCaptchaParams(captchaV2) };
    }

    const { success, error, errorMessage } = await sendResetPasswordLink(body);

    if (!success) {
      handleError({ error, errorMessage });
    }

    onFinish(success);
    setLoading(false);
  };

  const yandexCaptcha = useYandexCaptcha({
    onChangeError: setError,
    onSubmit: onSubmitFinish,
  });

  const resetGoogleCaptchaField = () => {
    form.setFieldValue('captchaV2', null);
  };

  const handleError = ({ error, errorMessage }) => {
    const errorKey = error?.body?.key;

    switch (CAPTCHA_TYPE) {
      case CaptchaType.YANDEX: {
        const isCaptchaError = yandexCaptcha.checkIsCaptchaError(errorKey);

        if (isCaptchaError) {
          setError(errorKey);
          return;
        }

        break;
      }

      case CaptchaType.GOOGLE:
      default: {
        const captchaError = googleCaptcha.checkCaptchaError(error);
        const isCaptchaFinalError = checkIsCaptchaFinalError(error);

        googleCaptcha.resetCaptchaState();

        if (isCaptchaFinalError) {
          notificationApi.error({ title: t('requestErrors.captcha2Failed') });
          resetGoogleCaptchaField();
          return;
        }

        if (captchaError) {
          onGoogleCaptchaError(captchaError);
          resetGoogleCaptchaField();
          return;
        }
      }
    }

    setError(null);
    notificationApi.error({ title: t(errorMessage) });
  };

  const handleFinish = values => {
    switch (CAPTCHA_TYPE) {
      case CaptchaType.YANDEX:
        return yandexCaptcha.onSubmit(values);

      case CaptchaType.GOOGLE:
      default:
        return onSubmitFinish(values);
    }
  };

  const showGoogleCaptcha = CAPTCHA_TYPE === CaptchaType.GOOGLE && googleCaptcha.isCaptchaError;
  const showYandexCaptcha = CAPTCHA_TYPE === CaptchaType.YANDEX;

  const isYandexCaptchaError = yandexCaptcha.checkIsCaptchaError(error);
  const isGoogleCaptchaError = error === ErrorType.CAPTCHA_FINAL_ERROR;

  const showCaptchaErrorAlert = isYandexCaptchaError || isGoogleCaptchaError;

  const isSubmitButtonDisabled = useMemo(() => {
    switch (CAPTCHA_TYPE) {
      case CaptchaType.YANDEX:
        return submitDisabled || loading || isYandexCaptchaError;

      case CaptchaType.GOOGLE:
      default:
        return (
          submitDisabled ||
          loading ||
          !googleCaptcha.isCaptchaReady ||
          (googleCaptcha.captchaError && googleCaptcha.captchaError === ErrorType.CAPTCHA_FINAL_ERROR) ||
          (googleCaptcha.captchaError === GoogleCaptchaErrorType.FIRST_STEP_FAILED && !captchaV2)
        );
    }
  }, [
    captchaV2,
    googleCaptcha.captchaError,
    googleCaptcha.isCaptchaReady,
    isYandexCaptchaError,
    loading,
    submitDisabled,
  ]);

  return (
    <Form
      form={form}
      name="forgot-password"
      layout="vertical"
      requiredMark="optional"
      scrollToFirstError
      onFinish={handleFinish}
      className="np-forgot-password-form"
    >
      <Form.Item name="email" label={t('form.label.email')} rules={[rule.email, rule.required]}>
        <Input autoFocus disabled={loading} />
      </Form.Item>

      {showGoogleCaptcha && (
        <Form.Item name="captchaV2" validateFirst rules={[rule.required]}>
          <GoogleCaptcha key={googleCaptcha.key} />
        </Form.Item>
      )}

      {showYandexCaptcha && (
        <YandexCaptcha
          key={yandexCaptcha.key}
          visible={yandexCaptcha.visible}
          onChange={yandexCaptcha.onChange}
          onSuccess={token => onSubmitFinish({ ...form.getFieldsValue(), [Y_CAPTCHA_PARAM_KEY]: token })}
          onChangeStatus={yandexCaptcha.onChangeStatus}
          onChangeVisible={yandexCaptcha.onChangeVisible}
        />
      )}

      {showCaptchaErrorAlert && <CaptchaErrorAlert type={error} />}

      <Form.Item>
        <Button block loading={loading} disabled={isSubmitButtonDisabled} htmlType="submit">
          {t('form.button.sendLink')}
        </Button>
      </Form.Item>
    </Form>
  );
};

export default ForgotPasswordForm;
